import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useMutation } from "@apollo/client";
import moment from "moment";

import { Form } from "react-final-form";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import makeStyles from "@mui/styles/makeStyles";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

import CreateIncidentSharingForm from "../components/IncidentPropertyShareForm";
import IncidentSharesList from "../components/IncidentPropertyShare";
import Divider from "@mui/material/Divider";
import FormHelperText from "@mui/material/FormHelperText";

const CREATE_INCIDENT_SHARING_MUTATION = gql`
  mutation CreateIncidentSharing($input: CreateIncidentSharingInput!) {
    incidents {
      createSharing(input: $input) {
        success
        error {
          code
          message
        }
      }
    }
  }
`;

const REVOKE_INCIDENT_SHARING_MUTATION = gql`
  mutation RevokeIncidentSharing($input: RevokeIncidentSharingInput!) {
    incidents {
      revokeSharing(input: $input) {
        success
        error {
          code
          message
        }
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  marginTop: {
    marginTop: theme.spacing(2),
  },
  sharesSection: {
    marginTop: theme.spacing(3),
  },
  sharesScroll: {
    maxHeight: "40vh",
    overflowY: "auto",
    marginTop: theme.spacing(1),
  },
}));
const CreateIncidentSharingDialog = ({
  open,
  incidentId,
  shares,
  handleClose,
}) => {
  const classes = useStyles();

  const [message, setMessage] = useState({ type: null, text: "" });

  const [formValues, setFormValues] = useState({
    email: "",
    expiresAt: null,
  });

  const [createSharing, { loading: loadingCreateSharing }] = useMutation(
    CREATE_INCIDENT_SHARING_MUTATION,
    {
      refetchQueries: ["Node"],
    }
  );

  const [revokeSharing, { loading: loadingRevokeLoading }] = useMutation(
    REVOKE_INCIDENT_SHARING_MUTATION,
    {
      refetchQueries: ["Node"],
    }
  );

  useEffect(() => {
    if (open) {
      setMessage({ type: null, text: "" });
    }
  }, [open]);

  const handleFormSubmit = (data) => {
    const { email, expiresAt } = data;

    // Validate email
    if (!email || !email.trim()) {
      setMessage({ type: "error", text: "Email is required." });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setMessage({ type: "error", text: "Invalid email format." });
      return;
    }

    const expiryISO = expiresAt
      ? moment(expiresAt).endOf("day").utc().toISOString()
      : null;
    const variables = {
      input: {
        incidentId,
        emailShared: email.trim(),
        expiresAt: expiryISO,
      },
    };

    createSharing({ variables })
      .then(({ data }) => {
        const result = data?.incidents?.createSharing;

        if (result?.success) {
          setMessage({ type: "success", text: "Share created successfully." });

          return;
        }

        if (result?.error?.message === "AlreadyShared") {
          setMessage({
            type: "error",
            text: `Email address ${variables.input.emailShared} already shared.`,
          });
          return;
        }

        setMessage({
          type: "error",
          text: result?.error?.message || "Failed to create share.",
        });
      })
      .catch((err) => {
        setMessage({ type: "error", text: err.message || "Unexpected error." });
      });
  };

  const handleRevokeShare = (shareId) => {
    const variables = { input: { shareId } };

    revokeSharing({ variables })
      .then(({ data }) => {
        const result = data?.incidents?.revokeSharing;

        if (result?.success) {
          setMessage({ type: "success", text: "Share revoked." });

          return;
        }

        setMessage({
          type: "error",
          text: result?.error?.message || "Failed to revoke share.",
        });
      })
      .catch((err) =>
        setMessage({ type: "error", text: err.message || "Unexpected error." })
      );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <Form
          onSubmit={handleFormSubmit}
          render={({ handleSubmit, submitting, submitError }) => (
            <form onSubmit={handleSubmit}>
              <DialogTitle>Incident sharing</DialogTitle>
              <DialogContent dividers={true}>
                <CreateIncidentSharingForm
                  classes={classes}
                  submitting={submitting}
                  loading={loadingCreateSharing}
                />
                {message.type && (
                  <FormHelperText
                    style={{
                      color: message.type === "error" ? "red" : "green",
                      marginTop: 12,
                    }}
                  >
                    {message.text}
                  </FormHelperText>
                )}
                <div className={classes.sharesSection}>
                  <Divider />
                  <div className={classes.sharesScroll}>
                    <IncidentSharesList
                      shares={shares}
                      onRevokeShare={handleRevokeShare}
                    />
                  </div>
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  disabled={submitting || loadingCreateSharing}
                  onClick={handleClose}
                  color="primary"
                >
                  Close
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

export default CreateIncidentSharingDialog;
